import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import OnboardingWizard from './OnboardingWizard';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Building2, 
  Package, 
  FileText, 
  Users, 
  MessageSquare,
  Settings,
  LogOut,
  Sun,
  Moon,
  Globe,
  Menu,
  X,
  BarChart3,
  FolderOpen,
  ShieldCheck,
  UsersRound,
  Bell,
  Puzzle,
  Route,
  TrendingUp,
  LucideIcon,
  Sparkles,
  Shield,
  CreditCard,
  Wallet,
  History as HistoryIcon,
  Building2 as Building2Icon,
  PackageOpen,
  Briefcase,
  Search,
  AlertTriangle,
  UserPlus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { useExtensions } from '../hooks/useExtensions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
  menuKey?: string;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout, isBuyer, isSeller, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, locale, changeLocale } = useI18n();
  const { isExtensionActive } = useExtensions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const [notifications, setNotifications] = React.useState({
    unread_messages: 0,
    pending_orders: 0,
    pending_relationships: 0
  });
  const [inAppNotifications, setInAppNotifications] = React.useState({
    notifications: [],
    unread_count: 0
  });
  const [companyLogo, setCompanyLogo] = React.useState(null);
  const [companyName, setCompanyName] = React.useState('OPS!');
  const [associationLogo, setAssociationLogo] = React.useState<string | null>(null);
  const [associationName, setAssociationName] = React.useState<string | null>(null);
  const [menuConfig, setMenuConfig] = React.useState<Record<string, any> | null>(null);
  const [isAssociationUser, setIsAssociationUser] = React.useState(false);
  const [associationStatus, setAssociationStatus] = React.useState<{
    type: 'buyer_standard' | 'buyer_plus_associated' | 'associated_only';
    has_full_buyer_features: boolean;
    has_association_features: boolean;
    has_pending_request?: boolean;
  } | null>(null);
  const [offeringType, setOfferingType] = React.useState<'products' | 'services' | 'both'>('products');
  
  const isAdmin = user?.role === 'admin';

  // Check if onboarding should be shown
  React.useEffect(() => {
    if (user && !user.onboarding_completed && (isBuyer || isSeller)) {
      // Delay to let the page render first
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, isBuyer, isSeller]);

  React.useEffect(() => {
    fetchNotifications();
    fetchInAppNotifications();
    fetchCompanyInfo();
    fetchMenuConfig();
    
    // Listen for logo updates from settings page
    const handleLogoUpdate = (event: CustomEvent) => {
      const { logo_url } = event.detail;
      setCompanyLogo(logo_url);
    };
    
    // Listen for notification read events to update badge immediately
    const handleNotificationRead = () => {
      fetchInAppNotifications();
    };
    
    // Listen for order updates to refresh pending count
    const handleOrderUpdate = () => {
      fetchNotifications();
    };
    
    window.addEventListener('company-logo-updated', handleLogoUpdate as EventListener);
    window.addEventListener('notification-read', handleNotificationRead);
    window.addEventListener('order-updated', handleOrderUpdate);
    
    // Poll notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchInAppNotifications();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('company-logo-updated', handleLogoUpdate as EventListener);
      window.removeEventListener('notification-read', handleNotificationRead);
      window.removeEventListener('order-updated', handleOrderUpdate);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchInAppNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications/list?limit=10`);
      setInAppNotifications(res.data);
    } catch (error) {
      console.error('Error fetching in-app notifications:', error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      fetchInAppNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      fetchInAppNotifications();
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const fetchCompanyInfo = async () => {
    try {
      const res = await axios.get(`${API}/companies/my`);
      if (res.data) {
        setCompanyLogo(res.data.logo_url || null);
        // Usa display_name se disponibile, altrimenti name, altrimenti default
        setCompanyName(res.data.display_name || res.data.name || 'OPS!');
      }
    } catch (error) {
      // Company might not exist
    }
    
    // Carica branding associazione e stato utente
    try {
      const meRes = await axios.get(`${API}/auth/me`);
      if (meRes.data.association_branding) {
        const branding = meRes.data.association_branding;
        setAssociationLogo(branding.logo_url || null);
        setAssociationName(branding.name || null);
      }
      // Imposta lo stato di associazione per il menu dinamico
      if (meRes.data.association_status) {
        setAssociationStatus(meRes.data.association_status);
      }
    } catch (error) {
      // Ignora errori
    }
  };

  const fetchMenuConfig = async () => {
    try {
      const res = await axios.get(`${API}/associations/my-association/menu`);
      if (res.data.is_association_user) {
        setMenuConfig(res.data.menu);
        setIsAssociationUser(true);
      }
    } catch (error) {
      setIsAssociationUser(false);
    }
    // Carica offering_type dal tenant
    try {
      const labelsRes = await axios.get(`${API}/associations/my-association/labels`);
      // L'offering_type viene dalla tenant_config, lo espongo anche dalle labels
    } catch { /* ignore */ }
  };
  
  // Carica offering_type
  React.useEffect(() => {
    const loadOfferingType = async () => {
      try {
        const res = await axios.get(`${API}/associations/my-association/labels`);
        if (res.data.offering_type) setOfferingType(res.data.offering_type);
      } catch { /* ignore */ }
    };
    if (user?.association_id) loadOfferingType();
  }, [user?.association_id]);

  // Helper per verificare se una voce menu è visibile
  const isMenuItemVisible = (menuKey: string): boolean => {
    if (!isAssociationUser || !menuConfig) return true; // Default: mostra tutto
    const item = menuConfig[menuKey];
    return item?.visible !== false;
  };

  // Helper per ottenere la label personalizzata di una voce menu
  const getMenuLabel = (menuKey: string, defaultLabel: string): string => {
    if (!isAssociationUser || !menuConfig) return defaultLabel;
    const item = menuConfig[menuKey];
    return item?.label || defaultLabel;
  };

  // Menu per buyer con associazione admin
  const isAssociationAdmin = user?.is_association_admin && user?.association_id;
  const hasAssociation = !!user?.association_id;
  
  // Determina le funzionalità in base allo stato associazione
  const hasFullBuyerFeatures = associationStatus?.has_full_buyer_features ?? true;
  const hasAssociationFeatures = associationStatus?.has_association_features ?? hasAssociation;
  const isAssociatedOnly = associationStatus?.type === 'associated_only';
  
  // Definizione completa delle voci menu buyer - poi filtrate in base al tipo utente
  const allBuyerNavItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: getMenuLabel('dashboard', t('nav.dashboard')), menuKey: 'dashboard' },
    
    // === FUNZIONI ASSOCIAZIONE (visibili se ha features associazione) ===
    ...(hasAssociationFeatures ? [
      { to: '/groupage', icon: PackageOpen, label: getMenuLabel('groupage', 'Groupage'), menuKey: 'groupage' }
    ] : []),
    
    // Admin Associazione items (sempre mostrati se admin)
    ...(isAssociationAdmin ? [
      { to: '/association/dashboard', icon: Building2Icon, label: getMenuLabel('gestione_associazione', 'Gestione Associazione'), menuKey: 'gestione_associazione' },
      { to: '/association/members', icon: Users, label: getMenuLabel('gestione_associati', 'Gestione Associati'), menuKey: 'gestione_associati' },
      { to: '/association/requests', icon: UserPlus, label: getMenuLabel('richieste_membership', 'Richieste Membership'), menuKey: 'richieste_membership' },
      { to: '/association/suppliers', icon: Package, label: getMenuLabel('fornitori_associazione', 'Fornitori Associazione'), menuKey: 'fornitori_associazione' },
      { to: '/association/settings', icon: Settings, label: getMenuLabel('impostazioni_associazione', 'Impostazioni Associazione'), menuKey: 'impostazioni_associazione' }
    ] : []),
    
    // === FUNZIONI BUYER STANDARD (visibili solo se ha full buyer features) ===
    ...(hasFullBuyerFeatures ? [
      { to: '/orders', icon: ShoppingCart, label: getMenuLabel('ordini', t('nav.orders')), badge: notifications.pending_orders, menuKey: 'ordini' },
      { to: '/directory', icon: Building2, label: getMenuLabel('directory_fornitori', t('nav.directory')), menuKey: 'directory_fornitori' },
      { to: '/relationships', icon: Users, label: getMenuLabel('fornitori', 'Fornitori'), menuKey: 'fornitori' },
      { to: '/stats', icon: BarChart3, label: getMenuLabel('statistiche', 'Statistiche'), menuKey: 'statistiche' },
    ] : []),
    
    // === FUNZIONI COMUNI A TUTTI I BUYER ===
    { to: '/chat', icon: MessageSquare, label: getMenuLabel('messaggi', t('nav.chat')), badge: notifications.unread_messages, menuKey: 'messaggi' },
    { to: '/notifications', icon: Bell, label: getMenuLabel('notifiche', 'Notifiche'), badge: inAppNotifications.unread_count, menuKey: 'notifiche' },
    
    // Team e Estensioni solo per buyer standard
    ...(hasFullBuyerFeatures ? [
      { to: '/team', icon: UsersRound, label: getMenuLabel('team', 'Team'), menuKey: 'team' },
      { to: '/store', icon: Sparkles, label: getMenuLabel('estensioni', 'Estensioni'), menuKey: 'estensioni' },
    ] : []),
    
    { to: '/settings', icon: Settings, label: getMenuLabel('impostazioni', t('nav.settings')), menuKey: 'impostazioni' },
  ];

  // Filtra in base alla configurazione menu dell'associazione
  const buyerNavItems: NavItem[] = allBuyerNavItems.filter(item => {
    if (!item.menuKey) return true;
    return isMenuItemVisible(item.menuKey);
  });

  const showProducts = offeringType === 'products' || offeringType === 'both' || !user?.association_id;
  const showServices = offeringType === 'services' || offeringType === 'both';

  const sellerNavItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    // Ordini: solo se il seller vende prodotti (non solo servizi)
    ...(showProducts ? [{ to: '/orders', icon: ShoppingCart, label: t('nav.orders'), badge: notifications.pending_orders }] : []),
    ...(showProducts ? [{ to: '/products', icon: Package, label: 'Catalogo Prodotti' }] : []),
    ...(showServices ? [{ to: '/services', icon: Briefcase, label: 'Catalogo Servizi' }] : []),
    { to: '/pricelists', icon: FileText, label: t('nav.pricelists') },
    { to: '/relationships', icon: Users, label: 'Clienti', badge: notifications.pending_relationships },
    // Crediti: solo con estensione fatturazione attiva
    ...(isExtensionActive('ext_billing') ? [{ to: '/credits', icon: Wallet, label: 'Crediti' }] : []),
    { to: '/seller-stats', icon: BarChart3, label: 'Statistiche' },
    { to: '/chat', icon: MessageSquare, label: t('nav.chat'), badge: notifications.unread_messages },
    { to: '/notifications', icon: Bell, label: 'Notifiche', badge: inAppNotifications.unread_count },
    // Percorsi Consegna: solo con estensione logistica attiva
    ...(isExtensionActive('ext_logistics') ? [{ to: '/delivery-routes', icon: Route, label: 'Percorsi Consegna' }] : []),
    { to: '/team', icon: UsersRound, label: 'Team' },
    { to: '/store', icon: Sparkles, label: 'Estensioni' },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const adminNavItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/admin/associations', icon: Building2Icon, label: 'Associazioni' },
    { to: '/admin/vat-lookup', icon: Search, label: 'VAT Lookup' },
    { to: '/admin/accreditation', icon: ShieldCheck, label: 'Accreditamenti' },
    { to: '/admin/extensions', icon: Puzzle, label: 'Estensioni' },
    { to: '/admin/payments', icon: CreditCard, label: 'Pagamenti' },
    { to: '/admin/notifications', icon: Bell, label: 'Notifiche' },
    { to: '/admin/errors', icon: AlertTriangle, label: 'Errori Sistema' },
    { to: '/admin/audit-logs', icon: FileText, label: 'Log Attività' },
    { to: '/admin/gdpr', icon: Shield, label: 'GDPR' },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const navItems = isAdmin ? adminNavItems : (isSeller ? sellerNavItems : buyerNavItems);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50
        w-64 bg-card border-r border-border
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-screen overflow-hidden">
          {/* Logo */}
          <div className="min-h-[80px] flex items-center justify-between px-3 py-3 border-b border-border">
            <div className="flex items-center min-w-0 flex-1">
              {/* Mostra logo/nome associazione se disponibile, altrimenti azienda */}
              {associationLogo ? (
                <img 
                  src={associationLogo} 
                  alt={associationName || 'Associazione'}
                  className="h-auto w-full max-h-[64px] object-contain"
                />
              ) : associationName ? (
                <span className="font-heading text-lg font-bold text-primary truncate">
                  {associationName}
                </span>
              ) : companyLogo ? (
                <img 
                  src={companyLogo} 
                  alt={companyName}
                  className="h-auto w-full max-h-[64px] object-contain"
                />
              ) : (
                <span className="font-heading text-lg font-bold text-primary truncate">
                  {companyName}
                </span>
              )}
            </div>
            <button 
              className="lg:hidden p-2 hover:bg-muted rounded-md shrink-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation - scrollabile */}
          <div className="flex-1 min-h-0 overflow-y-auto py-2">
            <nav className="px-3 space-y-0.5">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center justify-between px-3 py-1.5 rounded-md text-sm font-medium
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                  data-testid={`nav-${item.to.slice(1)}`}
                >
                  <div className="flex items-center gap-2">
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="min-w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* User section - fisso in basso */}
          <div className="flex-shrink-0 p-4 border-t border-border bg-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full justify-start gap-2"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content - con margine sinistro per compensare sidebar fissa */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-8 border-b border-border bg-card/50 backdrop-blur-xs sticky top-0 z-30">
          <button 
            className="lg:hidden p-2 hover:bg-muted rounded-md"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            {/* Notifications bell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="h-5 w-5" />
                  {inAppNotifications.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                      {inAppNotifications.unread_count > 99 ? '99+' : inAppNotifications.unread_count}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="font-medium">Notifiche</span>
                  {inAppNotifications.unread_count > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllNotificationsRead}>
                      Segna tutte come lette
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {inAppNotifications.notifications?.length > 0 ? (
                    inAppNotifications.notifications.map((notif) => (
                      <DropdownMenuItem 
                        key={notif.id} 
                        className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!notif.read ? 'bg-primary/5' : ''}`}
                        onClick={() => {
                          if (!notif.read) markNotificationRead(notif.id);
                          if (notif.action_url) navigate(notif.action_url);
                        }}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {!notif.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                          <span className="font-medium text-sm truncate">{notif.title}</span>
                        </div>
                        <span className="text-xs text-muted-foreground line-clamp-2">{notif.message}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(notif.created_at).toLocaleDateString()} {new Date(notif.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      Nessuna notifica
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="language-toggle">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLocale('it')}>
                  <span className={locale === 'it' ? 'font-semibold' : ''}>Italiano</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLocale('en')}>
                  <span className={locale === 'en' ? 'font-semibold' : ''}>English</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>

      {/* Onboarding Wizard */}
      <OnboardingWizard 
        open={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
      />
    </div>
  );
};

export default DashboardLayout;
